import { React, useContext } from "react";

import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";

const CustomDataGrid = ({
  handleEditCell,
  showSpecficTest,
  setToRemove,
  columns,
  handleRemoveButton,
  inProgressMode,
  viewMode,
  selectedAccountId,
  quickFilterValues = [],
  quickFilterValue = "",
  setQuickFilterValue = () => {},
  getFilteredCount = () => null,
  getTotalCount = () => null,
  multiFilters = [],
  logicOperator = "or",
  filteredTrades = null, // New prop to receive pre-filtered trades
  onRefresh = null, // Callback to refresh data without page reload
  onClearBacktest = null, // Callback to clear backtest selection
}) => {
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);

  const backtestingMode = auth.backtestingMode;

  const handleRefresh = () => {
    // Clear trade and forecast related localStorage
    localStorage.removeItem("linked-trade-ids");
    localStorage.removeItem("trade-to-view");
    localStorage.removeItem("rotater");
    // localStorage.removeItem("backtest-active");
    // localStorage.removeItem("backtest-mode");
    // localStorage.removeItem("pre-backtest-account-id");

    // Clear backtest dropdown selection
    if (onClearBacktest) {
      onClearBacktest();
    }

    // Call parent's refresh callback if provided
    if (onRefresh) {
      onRefresh();
    }
  };

  // Advanced filtering function that handles both quick search and multi-filters
  const applyAdvancedFilter = (trades) => {
    let filteredTrades = [...trades];

    // Apply quick search filter if present
    if (
      quickFilterValues &&
      quickFilterValues.length > 0 &&
      quickFilterValues[0]
    ) {
      const searchValue = quickFilterValues[0].toLowerCase();

      filteredTrades = filteredTrades.filter((trade) => {
        // Search only core trade fields
        const searchableFields = [
          "pair",
          "direction",
          "outcomePC",
          "outcomeIC",
          "grade",
          "betaPattern",
          "alphaPattern",
          "entryType",
          "firstEntryOrScaleIn",
          "highlightedPair",
        ];

        // Check if search value exists in any field
        return searchableFields.some((field) => {
          const value = trade[field];
          if (value !== null && value !== undefined) {
            return value.toString().toLowerCase().includes(searchValue);
          }
          return false;
        });
      });
    }

    // Apply multi-filters if present
    if (multiFilters && multiFilters.length > 0) {
      filteredTrades = filteredTrades.filter((trade) => {
        const filterResults = multiFilters.map((filter) => {
          const fieldValue = trade[filter.field];
          const filterValue = filter.value;

          if (filterValue === "") return false;

          // Special handling for duplicatedFrom (Multi-Account)
          if (filter.field === "duplicatedFrom") {
            const hasDuplicatedFrom = fieldValue && fieldValue !== "";
            if (filterValue === "Yes") return hasDuplicatedFrom;
            if (filterValue === "No") return !hasDuplicatedFrom;
            return false;
          }

          // Special handling for foreCastId (Forecast)
          if (filter.field === "foreCastId") {
            const hasForecast =
              fieldValue && fieldValue !== "" && fieldValue !== "";
            if (filterValue === "Yes") return hasForecast;
            if (filterValue === "No") return !hasForecast;
            return false;
          }

          // Special handling for entryDateTime (combined entryDate + entryTime)
          if (filter.field === "entryDateTime") {
            const entryDate = trade.entryDate || "";
            const entryTime = trade.entryTime || "";
            // Parse trade date format "11/06/26" to "2026-06-11" and time "14:30"
            let tradeDateTime = "";
            if (entryDate) {
              const [day, month, year] = entryDate.split("/");
              const fullYear = year?.length === 2 ? `20${year}` : year;
              tradeDateTime = `${fullYear}-${month}-${day}`;
              if (entryTime) {
                tradeDateTime += ` ${entryTime}`;
              }
            }

            // Handle between operator for date ranges
            if (filter.operator === "between" && filterValue.includes(",")) {
              const [filterStart, filterEnd] = filterValue.split(",");
              const tradeTime = new Date(tradeDateTime).getTime();
              const startTime = new Date(filterStart).getTime();
              const endTime = new Date(filterEnd).getTime();
              return tradeTime >= startTime && tradeTime <= endTime;
            }

            // Single date comparison
            const tradeTime = new Date(tradeDateTime).getTime();
            const filterTime = new Date(filterValue).getTime();

            switch (filter.operator) {
              case "equals":
                // Compare just the date part
                return (
                  tradeDateTime.split(" ")[0] === filterValue.split(" ")[0]
                );
              case "after":
                return tradeTime > filterTime;
              case "before":
                return tradeTime < filterTime;
              default:
                return tradeDateTime
                  .toLowerCase()
                  .includes(filterValue.toLowerCase());
            }
          }

          // Special handling for exitDateTime (combined exitDate + exitTime)
          if (filter.field === "exitDateTime") {
            const exitDate = trade.exitDate || "";
            const exitTime = trade.exitTime || "";
            // Parse trade date format "11/06/26" to "2026-06-11" and time "14:30"
            let tradeDateTime = "";
            if (exitDate) {
              const [day, month, year] = exitDate.split("/");
              const fullYear = year?.length === 2 ? `20${year}` : year;
              tradeDateTime = `${fullYear}-${month}-${day}`;
              if (exitTime) {
                tradeDateTime += ` ${exitTime}`;
              }
            }

            // Handle between operator for date ranges
            if (filter.operator === "between" && filterValue.includes(",")) {
              const [filterStart, filterEnd] = filterValue.split(",");
              const tradeTime = new Date(tradeDateTime).getTime();
              const startTime = new Date(filterStart).getTime();
              const endTime = new Date(filterEnd).getTime();
              return tradeTime >= startTime && tradeTime <= endTime;
            }

            // Single date comparison
            const tradeTime = new Date(tradeDateTime).getTime();
            const filterTime = new Date(filterValue).getTime();

            switch (filter.operator) {
              case "equals":
                // Compare just the date part
                return (
                  tradeDateTime.split(" ")[0] === filterValue.split(" ")[0]
                );
              case "after":
                return tradeTime > filterTime;
              case "before":
                return tradeTime < filterTime;
              default:
                return tradeDateTime
                  .toLowerCase()
                  .includes(filterValue.toLowerCase());
            }
          }

          // Special handling for risk field
          if (filter.field === "risk") {
            const tradeRisk = parseFloat(trade.risk);
            if (isNaN(tradeRisk)) return false;

            if (filter.operator === "between" && filterValue.includes(",")) {
              const [min, max] = filterValue
                .split(",")
                .map((v) => parseFloat(v.trim()));
              return tradeRisk >= min && tradeRisk <= max;
            }
            const filterNum = parseFloat(filterValue);
            switch (filter.operator) {
              case "equals":
                return tradeRisk === filterNum;
              case "greaterThan":
                return tradeRisk > filterNum;
              case "lessThan":
                return tradeRisk < filterNum;
              default:
                return false;
            }
          }

          // Special handling for stopLoss field
          if (filter.field === "stopLoss") {
            const sl = parseFloat(
              trade.stopLossIC ||
                trade.stopLossPC ||
                trade.stopLossPriceIC ||
                trade.stopLossPricePC,
            );
            if (isNaN(sl)) return false;

            if (filter.operator === "between" && filterValue.includes(",")) {
              const [min, max] = filterValue
                .split(",")
                .map((v) => parseFloat(v.trim()));
              return sl >= min && sl <= max;
            }
            const filterNum = parseFloat(filterValue);
            switch (filter.operator) {
              case "equals":
                return sl === filterNum;
              case "greaterThan":
                return sl > filterNum;
              case "lessThan":
                return sl < filterNum;
              default:
                return false;
            }
          }

          // Special handling for P/L field
          if (filter.field === "pl") {
            // Check if filter value specifies dollar or percentage mode
            const isPercent = filterValue.startsWith("pct:");
            const valueStr = isPercent
              ? filterValue.replace("pct:", "")
              : filterValue.replace("$:", "");

            if (filter.operator === "between" && valueStr.includes(",")) {
              const [min, max] = valueStr
                .split(",")
                .map((v) => parseFloat(v.trim()));
              if (isPercent) {
                const plPct = parseFloat(
                  trade.profitLossPercentagePC ??
                    trade.profitLossPercentageIC ??
                    0,
                );
                return !isNaN(plPct) && plPct >= min && plPct <= max;
              } else {
                const plDollar = parseFloat(
                  trade.profitLossDollarPersonal ??
                    trade.profitLossDollarInvestor ??
                    0,
                );
                return !isNaN(plDollar) && plDollar >= min && plDollar <= max;
              }
            }

            const filterNum = parseFloat(valueStr);
            switch (filter.operator) {
              case "equals":
                if (isPercent) {
                  const plPct = parseFloat(
                    trade.profitLossPercentagePC ??
                      trade.profitLossPercentageIC ??
                      0,
                  );
                  return !isNaN(plPct) && plPct === filterNum;
                } else {
                  const plDollar = parseFloat(
                    trade.profitLossDollarPersonal ??
                      trade.profitLossDollarInvestor ??
                      0,
                  );
                  return !isNaN(plDollar) && plDollar === filterNum;
                }
              case "greaterThan":
                if (isPercent) {
                  const plPct = parseFloat(
                    trade.profitLossPercentagePC ??
                      trade.profitLossPercentageIC ??
                      0,
                  );
                  return !isNaN(plPct) && plPct > filterNum;
                } else {
                  const plDollar = parseFloat(
                    trade.profitLossDollarPersonal ??
                      trade.profitLossDollarInvestor ??
                      0,
                  );
                  return !isNaN(plDollar) && plDollar > filterNum;
                }
              case "lessThan":
                if (isPercent) {
                  const plPct = parseFloat(
                    trade.profitLossPercentagePC ??
                      trade.profitLossPercentageIC ??
                      0,
                  );
                  return !isNaN(plPct) && plPct < filterNum;
                } else {
                  const plDollar = parseFloat(
                    trade.profitLossDollarPersonal ??
                      trade.profitLossDollarInvestor ??
                      0,
                  );
                  return !isNaN(plDollar) && plDollar < filterNum;
                }
              default:
                return false;
            }
          }

          if (!fieldValue) return false;

          switch (filter.operator) {
            case "contains":
              return fieldValue
                .toString()
                .toLowerCase()
                .includes(filterValue.toString().toLowerCase());
            case "equals":
              return (
                fieldValue.toString().toLowerCase() ===
                filterValue.toString().toLowerCase()
              );
            case "startsWith":
              return fieldValue
                .toString()
                .toLowerCase()
                .startsWith(filterValue.toString().toLowerCase());
            case "endsWith":
              return fieldValue
                .toString()
                .toLowerCase()
                .endsWith(filterValue.toString().toLowerCase());
            case "notContains":
              return !fieldValue
                .toString()
                .toLowerCase()
                .includes(filterValue.toString().toLowerCase());
            case "notEquals":
              return (
                fieldValue.toString().toLowerCase() !==
                filterValue.toString().toLowerCase()
              );
            case "after":
              return new Date(fieldValue) > new Date(filterValue);
            case "before":
              return new Date(fieldValue) < new Date(filterValue);
            case "greaterThan":
              return parseFloat(fieldValue) > parseFloat(filterValue);
            case "lessThan":
              return parseFloat(fieldValue) < parseFloat(filterValue);
            case "between":
              if (Array.isArray(filter.value) && filter.value.length === 2) {
                const date = new Date(fieldValue);
                const start = new Date(filter.value[0]);
                const end = new Date(filter.value[1]);
                return date >= start && date <= end;
              } else if (
                typeof filterValue === "string" &&
                filterValue.includes(",")
              ) {
                // Handle numeric range (e.g., "100,500")
                const [min, max] = filterValue
                  .split(",")
                  .map((v) => parseFloat(v.trim()));
                const numValue = parseFloat(fieldValue);
                return numValue >= min && numValue <= max;
              }
              return false;
            default:
              return fieldValue
                .toString()
                .toLowerCase()
                .includes(filterValue.toString().toLowerCase());
          }
        });

        // Apply AND/OR logic
        if (logicOperator === "and") {
          return filterResults.every((result) => result);
        } else {
          return filterResults.some((result) => result);
        }
      });
    }

    return filteredTrades;
  };

  // Filter trades by selected account
  const filterByAccount = (trades) => {
    if (!selectedAccountId || selectedAccountId === "All") {
      return trades;
    }

    if (selectedAccountId === "All Personal") {
      return trades.filter((trade) => trade.equityType === "Personal Capital");
    }

    if (selectedAccountId === "All Investor") {
      return trades.filter((trade) => trade.equityType === "Investor Capital");
    }

    // Filter by specific account ID
    // For backward compatibility: if trade has accountId, match it directly
    // Otherwise, match by equityType based on the account's type
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const capitalAccounts = user.capitalAccounts || [];
    const selectedAccount = capitalAccounts.find(
      (acc) => acc.id === selectedAccountId,
    );

    if (!selectedAccount) {
      return trades;
    }

    return trades.filter((trade) => {
      // If trade has accountId field, use it
      if (trade.accountId) {
        return trade.accountId === selectedAccountId;
      }
      // Otherwise, fall back to equityType matching
      if (selectedAccount.type === "Personal") {
        return trade.equityType === "Personal Capital";
      } else if (selectedAccount.type === "Investor") {
        return trade.equityType === "Investor Capital";
      }
      return false;
    });
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <DataGrid
        sx={{
          width: "100%",
          minWidth: "100%",
          height: "100%",
          border: "none",
          "& .MuiSvgIcon-root": {
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
          },

          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
            minHeight: "calc(100% - 12px)",
            "&::-webkit-scrollbar": {
              height: "12px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                theme[0] === "dark" ? "rgba(60, 62, 66)" : "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme[0] === "dark" ? "rgba(120, 120, 120)" : "#888",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor:
                  theme[0] === "dark" ? "rgba(140, 140, 140)" : "#555",
              },
            },
          },

          backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#FCFCFF",

          color: theme[0] === "dark" ? "#FCFCFF" : "black",

          "& .MuiDataGrid-row--lastVisible": {
            borderBottom:
              theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
            borderBottom:
              theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
            minHeight: "40px !important",
            maxHeight: "40px !important",
          },
          "& .MuiDataGrid-columnHeader": {
            color: theme[0] === "dark" ? "#c9d1d9" : "#374151",
            fontWeight: "600",
            padding: "0 8px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: "14px",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-row": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell[data-field='pair']": {
            position: "sticky",
            left: 0,
            backgroundColor:
              theme[0] === "dark" ? "#1e1f23 !important" : "#FCFCFF !important",
            zIndex: 5,
            transform: "translateZ(0)",
            willChange: "transform",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='pair']": {
            backgroundColor:
              theme[0] === "dark" ? "#2a2b30 !important" : "#f5f5f5 !important",
          },
          "& .MuiDataGrid-columnHeader[data-field='pair']": {
            position: "sticky",
            left: 0,
            backgroundColor:
              theme[0] === "dark" ? "#25262b !important" : "#f9fafb !important",
            zIndex: 6,
          },
          "& .MuiDataGrid-main": {
            overflow: "visible",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
            borderTop:
              theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
            "@media (max-width: 768px)": {
              paddingRight: "40px",
            },
          },
          "& .MuiDataGrid-checkboxInput": {
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
          },

          "& .MuiTablePagination-displayedRows": {
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
          },
          "& .MuiTablePagination-actions": {
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
            background: "1px solid red",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor:
              theme[0] === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.04)",
          },
          "& .Mui-selected": {
            backgroundColor: "rgba(0, 73, 251, 0.30) !important",
          },
          "& .MuiDataGrid-overlay": {
            backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#FCFCFF",
          },
          "& .MuiDataGrid-overlayWrapper": {
            backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#FCFCFF",
          },
        }}
        onCellEditCommit={(params, o) => {
          return handleEditCell(params);
        }}
        // onStateChange={(state) => {
        //   console.log("rows", state.filter.filteredRowsLookup);
        // }}
        // sort my earliest trades first
        rows={
          filteredTrades !== null
            ? applyAdvancedFilter(filteredTrades).reverse()
            : !backtestingMode && inProgressMode
              ? applyAdvancedFilter(
                  filterByAccount(
                    auth.allTrades
                      .filter((trades) => trades.id?.includes("live-"))
                      .filter(
                        (trades) =>
                          trades.outcomePC?.includes("In Progress") ||
                          trades.outcomeIC?.includes("In Progress"),
                      ),
                  ),
                ).reverse()
              : !backtestingMode && !inProgressMode
                ? applyAdvancedFilter(
                    filterByAccount(
                      auth.allTrades.filter((trades) =>
                        trades.id?.includes("live-"),
                      ),
                    ),
                  ).reverse()
                : backtestingMode
                  ? applyAdvancedFilter(
                      auth.allTrades.filter(
                        (trades) =>
                          trades.testId?.includes(showSpecficTest) &&
                          trades.id?.includes("backtest-"),
                      ),
                    ).reverse()
                  : applyAdvancedFilter(
                      auth.allTrades.filter((trades) =>
                        trades.id?.includes("backtest-"),
                      ),
                    ).reverse()
        }
        columns={columns}
        disableVirtualization
        autoPageSize
        rowHeight={55}
        columnHeaderHeight={10}
        checkboxSelection={
          localStorage.getItem("view-mode") === "all-view" ||
          localStorage.getItem("view-mode") === "edit-view"
            ? true
            : false
        }
        // checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids, other) => {
          const selectedIDs = new Set(ids);
          const selectedRowData = auth.allTrades.filter((row) =>
            selectedIDs.has(row.id.toString()),
          );
          setToRemove(selectedRowData);
          handleRemoveButton(selectedRowData);
        }}
        components={{
          Footer: () => (
            <GridFooterContainer
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0,
              }}
            >
              <IconButton
                onClick={handleRefresh}
                title="Refresh"
                sx={{
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "&:hover": {
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
              <GridPagination />
            </GridFooterContainer>
          ),
        }}
      />
    </Box>
  );
};

export default CustomDataGrid;
